import React, { useState } from "react";
import { Star, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { product_id: "", author_name: "", rating: 5, title: "", body: "", age_group: "", verified: true };
const AGE_GROUPS = ["", "Under 5", "5–7 yrs", "7 yrs", "8–12 yrs", "Teen", "Parent", "Grandparent", "40+", "Teacher"];

// Admin: add a review you've received elsewhere (Facebook, in person, email). Always published as "Verified buyer".
export default function AddReviewCard({ products, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.author_name.trim() || !form.body.trim()) { toast.error("Pick a product and fill in the name and review."); return; }
    setBusy(true);
    try {
      await api.post("/admin/reviews", form);
      toast.success("Review added");
      setForm(EMPTY); setOpen(false); onAdded?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add review"); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-xl border border-[#19324A]/15 px-3 py-2 text-sm";
  return (
    <div className="bg-white rounded-3xl soft-shadow p-5 mb-6" data-testid="add-review-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-[#19324A]">Add a reader review</h3>
          <p className="text-xs text-[#19324A]/55">Got a lovely review on Facebook or in person? Post it here — it shows with a "Reader review" badge.</p>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="btn-pink !py-2 text-sm" data-testid="add-review-toggle"><Plus className="w-4 h-4" /> {open ? "Close" : "Add review"}</button>
      </div>
      {open && (
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="add-review-form">
          <select value={form.product_id} onChange={set("product_id")} className={field} data-testid="add-review-product">
            <option value="">Product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="flex items-center gap-1" data-testid="add-review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setForm((f) => ({ ...f, rating: n }))} aria-label={`${n} stars`} data-testid={`add-review-star-${n}`}>
                <Star className={`w-7 h-7 ${n <= form.rating ? "fill-[#FFD93D] text-[#FFD93D]" : "text-[#19324A]/20"}`} />
              </button>
            ))}
          </div>
          <input value={form.author_name} onChange={set("author_name")} placeholder="Reviewer name (e.g. Natty J)" className={field} data-testid="add-review-name" />
          <select value={form.age_group} onChange={set("age_group")} className={field} data-testid="add-review-age">
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a || "Age group (optional)"}</option>)}
          </select>
          <input value={form.title} onChange={set("title")} placeholder="Review heading (optional)" className={`${field} sm:col-span-2`} data-testid="add-review-title" />
          <textarea value={form.body} onChange={set("body")} rows={5} placeholder="The review…" className={`${field} sm:col-span-2`} data-testid="add-review-body" />
          <p className="flex items-center gap-2 text-sm text-[#19324A]/75 sm:col-span-2" data-testid="add-review-verified-note">
            <Check className="w-4 h-4 text-[#7ED957]" /> Admin reviews are published straight away as "Verified buyer" — the purchase check only applies to reviews submitted on the website.
          </p>
          <button type="submit" disabled={busy} className="btn-primary !py-2 text-sm justify-self-end" data-testid="add-review-submit">{busy ? "Adding…" : "Publish review"}</button>
        </form>
      )}
    </div>
  );
}
